import type { AvailabilityResult, PlainDate, PoolBreakdown } from '@paybae/shared'

/**
 * Cliente de la API.
 *
 * Todo va al mismo origen: Caddy sirve la app y la API bajo el mismo dominio,
 * así que la cookie de sesión viaja sola y no hay CORS que configurar. En
 * desarrollo, Vite hace de proxy hacia :3000 y el efecto es el mismo.
 *
 * `credentials: 'include'` está en todas las peticiones porque la sesión vive en
 * una cookie httpOnly: el token no es accesible desde JavaScript, que es
 * justamente lo que impide que un script inyectado se la lleve.
 */

export interface FieldErrors {
  [field: string]: string[]
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors?: FieldErrors,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** La sesión caducó o nunca existió: toca mandar al acceso. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** Autenticado, pero sin permiso. Típico del rol limitado. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** Sin red. Se distingue de un error del servidor para poder ofrecer reintento. */
  get isOffline(): boolean {
    return this.status === 0
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options

  let response: Response
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch {
    // Fallo de red: el mensaje tiene que decir qué hacer, no solo qué pasó.
    throw new ApiError(0, 'Sin conexión. Revisa tu red e inténtalo de nuevo.')
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const message =
      (payload as { message?: string } | null)?.message ?? 'Algo salió mal. Inténtalo de nuevo.'
    const fieldErrors = (payload as { fieldErrors?: FieldErrors } | null)?.fieldErrors

    // Una sesión que caduca a media navegación deja al usuario mirando un
    // «no pudimos cargar» con un botón de reintentar que no va a funcionar
    // nunca: la sesión no vuelve por insistir. Se avisa una sola vez desde
    // aquí y quien la gestiona decide —hoy, mandar al acceso—.
    //
    // Las propias rutas de sesión quedan fuera: `me` responde 401 cuando no hay
    // nadie dentro, que es su respuesta normal al arrancar, y `login` cuando la
    // contraseña no es la buena. Avisar en esos dos casos sería un bucle.
    if (response.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent(SESION_CADUCADA))
    }

    throw new ApiError(response.status, message, fieldErrors)
  }

  return payload as T
}

/** Aviso de que el servidor dejó de reconocer la sesión a media navegación. */
export const SESION_CADUCADA = 'paybae:sesion-caducada'

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  /** Sustituye el recurso entero. Lo usa el reparto, que se guarda completo. */
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

// ═══════════════════════════════════════════════════════════════════════════
//  Tipos compartidos con el servidor
// ═══════════════════════════════════════════════════════════════════════════

export type HouseholdKind = 'PERSONAL' | 'COUPLE' | 'GROUP'
export type MemberRole = 'ADMIN' | 'MEMBER' | 'LIMITED'
export type AccountType = 'CHECKING' | 'CASH' | 'SAVINGS' | 'MEAL_VOUCHER' | 'INVESTMENT'
export type CardBrand = 'VISA' | 'MASTERCARD' | 'AMEX' | 'OTHER'
/** `IRREGULAR` = sin calendario: llega cuando llega, y no se proyecta. */
export type PayFrequency = 'SEMI_MONTHLY' | 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'IRREGULAR'
export type NonBusinessDayRule = 'PREVIOUS_BUSINESS_DAY' | 'NEXT_BUSINESS_DAY' | 'EXACT'
export type PaymentSource = 'ACCOUNT' | 'CREDIT_CARD'
export type SplitMode = 'PERSONAL' | 'SHARED_EQUAL' | 'SHARED_CUSTOM'
export type CategoryGroup = 'FIXED' | 'VARIABLE' | 'DESPENSA' | 'DEBT' | 'SAVINGS' | 'INCOME'
export type StatementUrgency = 'OVERDUE' | 'DUE_TODAY' | 'DUE_SOON' | 'SCHEDULED'

export interface Household {
  id: string
  name: string
  kind: HouseholdKind
  currency: string
  timezone: string
  locale: string
  countExpectedIncome: boolean
  /**
   * Día del mes en que arranca el periodo del hogar. `1` es el mes natural.
   *
   * Las pantallas que calculan el periodo por su cuenta —movimientos, pagos del
   * periodo— tienen que pasárselo a `householdPeriod()`: sin él pintarían el mes
   * natural mientras el servidor mide otra ventana.
   */
  periodAnchorDay: number
}

/** Género gramatical con el que la app se dirige a cada quien. */
export type Treatment = 'FEMININE' | 'MASCULINE' | 'NEUTRAL'

export interface Member {
  id: string
  role: MemberRole
  personColorToken: string
  /** Cómo prefiere que le llamen. Si es `null`, se usa el nombre de la cuenta. */
  nickname: string | null
  treatment: Treatment
  fixedContributionCents: number
  joinedAt: string
  user: { id: string; displayName: string; email: string; avatarUrl: string | null }
}

/** El nombre con el que hay que dirigirse a alguien en pantalla. */
export function nombreDe(m: {
  nickname?: string | null
  user?: { displayName: string }
  displayName?: string
}): string {
  return m.nickname || m.user?.displayName || m.displayName || 'Sin nombre'
}

export interface Membership {
  id: string
  role: MemberRole
  personColorToken: string
  fixedContributionCents: number
  household: Household
}

export interface Profile {
  id: string
  email: string
  displayName: string
  avatarUrl: string | null
  memberships: Membership[]
  /**
   * Cuál de los hogares está mirando.
   *
   * Lo resuelve el servidor, no el cliente: pertenecer a varios es normal, y
   * que la app eligiera «el primero» por su cuenta sería una segunda regla que
   * acabaría desviándose de la del guard —que es la que decide qué datos
   * devuelve cada endpoint—.
   */
  activeMembershipId: string | null
}

/**
 * De qué está hecha la deuda de una tarjeta.
 *
 * Sirve para explicar en pantalla una línea de crédito que bajó sin ningún
 * movimiento detrás: la causa casi siempre es el capital a meses sin facturar.
 */
export interface CardBalanceBreakdown {
  /** Cortes ya cerrados con saldo pendiente. */
  billedUnpaidCents: number
  /** Consumo del ciclo en curso, con su mensualidad a meses incluida. */
  openCycleCents: number
  /** Capital de compras a meses cuyos cortes todavía no llegan. */
  unbilledInstallmentsCents: number
}

export interface Account {
  id: string
  ownerMemberId: string | null
  name: string
  type: AccountType
  institution: string | null
  balanceCents: number
  includeInAvailable: boolean
  colorToken: string | null
  icon: string | null
  archivedAt: string | null
  owner: { id: string; personColorToken: string; user: { displayName: string } } | null
  /**
   * Si arrastra movimientos, pagos, fijos o metas.
   *
   * Con historia solo se puede archivar: borrarla dejaría cada movimiento sin
   * responder «¿con qué se pagó?». Sin historia se puede borrar de verdad, que
   * es la salida para el alta equivocada.
   */
  hasHistory: boolean
}

export interface Category {
  id: string
  name: string
  group: CategoryGroup
  icon: string | null
  colorToken: string | null
  isDespensaEligible: boolean
  parentId: string | null
  archivedAt: string | null
}

export interface StatementSummary {
  id: string
  cycleStart: PlainDate
  cutoffDate: PlainDate
  dueDate: PlainDate
  safePaymentDate: PlainDate
  isClosed: boolean
  /**
   * Solo el consumo del periodo: los movimientos que se cargaron a la tarjeta.
   * **No incluye las mensualidades** —esas viajan aparte, en
   * `installmentsCents`— porque un plan a meses no materializa movimientos.
   */
  chargesCents: number
  /**
   * Lo que las compras a meses le cobran a este corte. Junto con `chargesCents`
   * forma `closingBalanceCents`. Van separados a propósito: el consumo se puede
   * dejar de gastar el mes que viene y la mensualidad no, y sumarlos esconde
   * justo esa diferencia.
   */
  installmentsCents: number
  closingBalanceCents: number
  paidAmountCents: number
  balanceDueCents: number
  minimumPaymentCents: number | null
  status: string
  paidAt: string | null
  daysUntilCutoff: number
  countdown: {
    calendarDaysUntilDue: number
    businessDaysUntilDue: number
    daysUntilSafePayment: number
    urgency: StatementUrgency
  }
}

export interface CreditCard {
  id: string
  name: string
  issuer: string | null
  brand: CardBrand
  last4: string | null
  colorToken: string | null
  ownerMemberId: string | null
  owner: { memberId: string; displayName: string; personColorToken: string } | null
  creditLimitCents: number
  statementDay: number
  paymentTermDays: number
  paymentTermType: 'BUSINESS_DAYS' | 'CALENDAR_DAYS'
  cutoffInclusive: boolean
  archivedAt: string | null
  currentBalanceCents: number
  availableCreditCents: number
  utilizationPct: number
  /** De qué está hecho `currentBalanceCents`. */
  balanceBreakdown: CardBalanceBreakdown
  /** Ciclo en curso: donde cae un gasto de hoy. Vence dentro de mes y medio. */
  openStatement: StatementSummary | null
  /** Ciclo que ya cortó: lo que toca pagar ahora. De aquí salen los avisos. */
  closedStatement: StatementSummary | null
  pendingPaymentCents: number
}

export type TransactionType =
  | 'EXPENSE'
  | 'INCOME'
  | 'TRANSFER'
  | 'CARD_PAYMENT'
  | 'SAVINGS_CONTRIBUTION'
  | 'REFUND'

export interface Transaction {
  id: string
  type: TransactionType
  amountCents: number
  occurredOn: PlainDate
  description: string
  merchant: string | null
  notes: string | null
  categoryId: string | null
  category: { id: string; name: string; icon: string | null; group: CategoryGroup } | null
  attributedToMemberId: string | null
  attributedTo: { id: string; personColorToken: string; user: { displayName: string } } | null
  /** Quién lo capturó. Puede no ser la misma persona a la que se le atribuye. */
  createdBy?: { id: string; personColorToken: string; user: { displayName: string } } | null
  /** El corte de tarjeta donde cae, cuando se pagó a crédito. */
  cardStatement?: {
    id: string
    cutoffDate: PlainDate
    dueDate: PlainDate
    status: string
  } | null
  splitMode: SplitMode
  paymentSource: PaymentSource
  accountId: string | null
  account: { id: string; name: string; type: AccountType } | null
  creditCardId: string | null
  creditCard: { id: string; name: string; last4: string | null } | null
  cardStatementId: string | null
  /**
   * La deuda que la tarjeta ya traía al registrarla. Es deuda, no gasto del
   * mes: se lista en Movimientos pero no suma a «gastado» ni a la dona.
   * Opcional porque una API anterior a la migración no lo manda.
   */
  isOpeningBalance?: boolean
  /**
   * De dónde salió el dinero, parte por parte y de mayor a menor.
   *
   * La primera es la que da nombre al movimiento en las listas. Un pago
   * combinado —vales más tarjeta— trae dos, y son ellas y no las columnas
   * `accountId`/`creditCardId` las que dicen cuánto puso cada método.
   */
  payments?: Array<{
    id: string
    source: PaymentSource
    amountCents: number
    account: { id: string; name: string; type: AccountType } | null
    creditCard: { id: string; name: string; brand: CardBrand; last4: string | null } | null
  }>
  /** Comprobantes pegados al movimiento. Se sirven detrás de la sesión. */
  files?: Array<{
    id: string
    fileName: string
    mimeType: string
    sizeBytes: number
    createdAt: string
  }>
  createdAt: string
}

/**
 * Avance de una meta, tal y como lo calcula el servidor.
 *
 * `pendingTransferCents` es la parte aportada que todavía no se traspasó: está
 * comprometida —el disponible ya la descuenta— pero el dinero sigue en la
 * cuenta de origen. Sin ese dato, una meta al 40% parecería tener el dinero
 * guardado cuando no se ha movido un peso.
 */
export interface SavingsGoalProgress {
  /**
   * Nulos en un fondo sin monto objetivo —una reserva de emergencia, una bolsa
   * general—. El servidor devuelve `null` a propósito y no cero: un cero se
   * leería como «0% avanzado», y una reserva no avanza hacia ningún final.
   */
  percent: number | null
  remainingCents: number | null
  pendingTransferCents: number
  contributionCount: number
  lastContributionOn: PlainDate | null
  daysRemaining: number | null
  monthsRemaining: number | null
  requiredPerMonthCents: number | null
  requiredPerFortnightCents: number | null
  requiredPerDayCents: number | null
  overdue: boolean
}

export interface SavingsGoal {
  id: string
  name: string
  /** Qué clase de fondo es. Solo `GOAL` exige monto objetivo. */
  kind: FundKind
  icon: string | null
  colorToken: string | null
  /** Nulo en todo lo que no sea `GOAL`: ese fondo no se termina, se mantiene. */
  targetAmountCents: number | null
  targetDate: PlainDate | null
  /** Nulo = meta del fondo común. Con id = meta personal de ese integrante. */
  ownerMemberId: string | null
  linkedAccountId: string | null
  currentAmountCents: number
  priority: number
  status: 'ACTIVE' | 'ACHIEVED' | 'PAUSED' | 'ARCHIVED'
  progress: SavingsGoalProgress
}

export type RecurrenceFrequency =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'SEMI_MONTHLY'
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'YEARLY'

/**
 * `SHARED_CUSTOM` no aplica a un gasto fijo: su definición no tiene dónde
 * guardar los porcentajes de un reparto a medida, y el servidor lo rechaza.
 */
export type RecurringSplitMode = 'PERSONAL' | 'SHARED_EQUAL'

/** La definición tal cual se guarda, sin lo que la lista calcula encima. */
export interface RecurringDefinition {
  id: string
  name: string
  categoryId: string | null
  amountCents: number
  isVariableAmount: boolean
  estimateCents: number | null
  frequency: RecurrenceFrequency
  dayOfMonth: number | null
  dayOfWeek: number | null
  startsOn: PlainDate
  endsOn: PlainDate | null
  paymentSource: PaymentSource
  accountId: string | null
  creditCardId: string | null
  attributedToMemberId: string | null
  splitMode: RecurringSplitMode
  autoPost: boolean
  nextDueOn: PlainDate
  lastPostedOn: PlainDate | null
  active: boolean
  category: {
    id: string
    name: string
    icon: string | null
    colorToken: string | null
    isDespensaEligible: boolean
  } | null
  account: { id: string; name: string; type: AccountType } | null
  creditCard: {
    id: string
    name: string
    brand: CardBrand
    last4: string | null
    colorToken: string | null
  } | null
  attributedTo: {
    id: string
    personColorToken: string
    user: { displayName: string; avatarUrl: string | null }
  } | null
}

export interface RecurringExpense extends RecurringDefinition {
  /** Inicio del periodo en curso: el vencimiento que lo abrió. */
  periodStart: PlainDate
  /** Si la ocurrencia de este periodo ya quedó registrada como movimiento. */
  postedThisPeriod: boolean
  lastPostedTransaction: { id: string; occurredOn: PlainDate; amountCents: number } | null
  /** Negativo si el vencimiento ya pasó. */
  daysUntilDue: number
  overdue: boolean
  /** Va a tarjeta: ese dinero no sale del efectivo todavía. */
  paidWithCard: boolean
  /** Monto con el que se proyecta: el estimado si el recibo varía. */
  projectedAmountCents: number
}

export interface RecurringInput {
  name: string
  amountCents: number
  isVariableAmount?: boolean
  /** Obligatorio cuando `isVariableAmount` es `true`. */
  estimateCents?: number | null
  frequency: RecurrenceFrequency
  /** 1 a 31, o 0 para el último día del mes. */
  dayOfMonth?: number | null
  /** 0 (domingo) a 6 (sábado). Solo lo usan semanal y catorcenal. */
  dayOfWeek?: number | null
  startsOn: PlainDate
  endsOn?: PlainDate | null
  paymentSource: PaymentSource
  accountId?: string | null
  creditCardId?: string | null
  categoryId?: string | null
  attributedToMemberId?: string | null
  splitMode?: RecurringSplitMode
  autoPost?: boolean
}

export type RecurringPatch = Partial<RecurringInput> & {
  active?: boolean
  /** Corrección manual del próximo vencimiento. */
  nextDueOn?: PlainDate
}

export interface PostOccurrenceResult {
  /** `false` cuando el cargo ya existía: el reintento no duplica nada. */
  created: boolean
  transaction: { id: string; occurredOn: PlainDate; amountCents: number }
}

export interface CommitmentSegment {
  key: 'fixed' | 'cards' | 'savings' | 'free'
  amountCents: number
  percent: number
}

export interface DashboardResponse {
  today: PlainDate
  periodStart: PlainDate
  periodEnd: PlainDate
  currency: string
  householdKind: HouseholdKind
  countExpectedIncome: boolean
  role: MemberRole
  memberId: string
  availability: AvailabilityResult
  commitment: { scope: string; pool: PoolBreakdown; segments: CommitmentSegment[] }
  members: Array<{
    memberId: string
    displayName: string
    nickname: string | null
    personColorToken: string
    role: MemberRole
    fixedContributionCents: number
    contributionPendingCents: number
    /** Lo que ya salió en el periodo. No es lo mismo que lo comprometido. */
    spentCents: number
    savedCents: number
  }>
  /** Gastado y ahorrado del fondo común. `null` en hogares personales. */
  joint: { spentCents: number; savedCents: number } | null
  /**
   * Nóminas y facturaciones que vienen, ordenadas por fecha. Llegan más allá del
   * periodo en curso, así que quien las use tiene que acotar el rango él mismo.
   */
  upcomingIncomes: Array<{
    memberId: string
    incomeSourceId: string
    name: string
    kind: IncomeKind
    date: PlainDate
    amountCents: number
    /** Días naturales desde hoy; `0` es hoy mismo. */
    daysUntil: number
  }>
  upcomingCards?: Array<{
    cardId: string
    name: string
    last4: string | null
    dueDate: PlainDate
    balanceDueCents: number
    urgency: StatementUrgency
    ownerMemberId: string | null
  }>
  goals?: SavingsGoal[]
}

export interface Invitation {
  id: string
  token: string
  email: string | null
  intendedRole: MemberRole
  expiresAt: string
  maxUses: number
  useCount: number
  acceptedAt: string | null
  revokedAt: string | null
  url?: string
  /**
   * Si el correo llegó a salir. Solo viene al crear o al reenviar.
   *
   * Se devuelve en vez de suponerlo porque el enlace se crea aunque Mailgun
   * falle: decir «ya se la mandamos» cuando no salió deja a quien invita
   * esperando una respuesta que nunca va a llegar.
   */
  emailSent?: boolean
}

// ═══════════════════════════════════════════════════════════════════════════
//  Endpoints
// ═══════════════════════════════════════════════════════════════════════════

export const auth = {
  me: () => api.get<Profile>('/auth/me'),
  login: (email: string, password: string) => api.post<Profile>('/auth/login', { email, password }),
  register: (input: {
    email: string
    password: string
    displayName: string
    householdName: string
    householdKind: HouseholdKind
    /**
     * Se manda cuando el alta viene de un enlace de invitación. El servidor
     * solo lo usa para no dar la bienvenida a un hogar propio que esa persona
     * ni pidió ni va a usar: en cuanto acepta, acaba en el de quien la invitó.
     */
    invitationToken?: string
  }) => api.post<Profile>('/auth/register', input),
  logout: () => api.post<void>('/auth/logout'),

  /**
   * Pedir el enlace para recuperar el acceso.
   *
   * Responde 202 siempre, tenga cuenta ese correo o no. No es un descuido: si
   * distinguiera, este formulario diría qué correos están registrados en
   * PayBae, y aquí una cuenta guarda cuánto gana alguien. La pantalla dice lo
   * mismo que dice el servidor.
   */
  forgotPassword: (email: string) =>
    api.post<{ ok: true; message: string }>('/auth/password/forgot', { email }),

  /** ¿El enlace del correo sigue vivo? */
  checkResetToken: (token: string) =>
    api.get<{ valido: boolean; nombre?: string }>(
      `/auth/password/reset/${encodeURIComponent(token)}`,
    ),

  resetPassword: (token: string, password: string) =>
    api.post<{ ok: true; message: string }>('/auth/password/reset', { token, password }),
}

export const household = {
  get: () => api.get<Household & { members: Member[] }>('/household'),
  update: (input: {
    name?: string
    countExpectedIncome?: boolean
    timezone?: string
    /** Día en que arranca el periodo. El servidor lo acepta entre 1 y 28. */
    periodAnchorDay?: number
  }) =>
    api.patch<Household>('/household', input),
  /**
   * El propio perfil. Ruta aparte de `updateMember` porque no requiere ser
   * administrador: el apodo, el trato y el color son de quien los lleva.
   */
  /** Cambiar a qué hogar mirar. El servidor comprueba la pertenencia. */
  switch: (householdId: string) =>
    api.post<{ memberId: string; role: MemberRole; household: Household }>(
      '/household/switch',
      { householdId },
    ),
  updateSelf: (input: {
    nickname?: string | null
    treatment?: Treatment
    personColorToken?: string
  }) => api.patch<Member>('/household/me', input),
  updateMember: (
    memberId: string,
    input: {
      role?: MemberRole
      personColorToken?: string
      nickname?: string | null
      treatment?: Treatment
      fixedContributionCents?: number
    },
  ) => api.patch<Member>(`/household/members/${memberId}`, input),
  invitations: {
    list: () => api.get<Invitation[]>('/household/invitations'),
    create: (input: { email?: string; intendedRole?: MemberRole; expiresInDays?: number }) =>
      api.post<Invitation>('/household/invitations', input),
    /** Vuelve a mandar el correo sin cambiar el enlace ya compartido. */
    resend: (id: string) =>
      api.post<{ emailSent: boolean }>(`/household/invitations/${id}/resend`),
    revoke: (id: string) => api.delete<void>(`/household/invitations/${id}`),
  },
}

export const accounts = {
  list: (includeArchived = false) =>
    api.get<{ accounts: Account[]; summary?: unknown }>(
      `/accounts${includeArchived ? '?includeArchived=true' : ''}`,
    ),
  create: (input: {
    name: string
    type: AccountType
    ownerMemberId: string | null
    institution?: string
    initialBalanceCents?: number
    includeInAvailable?: boolean
  }) => api.post<Account>('/accounts', input),
  update: (id: string, input: Partial<Pick<Account, 'name' | 'institution' | 'includeInAvailable'>>) =>
    api.patch<Account>(`/accounts/${id}`, input),
  setBalance: (id: string, balanceCents: number) =>
    api.post<{ account: Account; deltaCents: number }>(`/accounts/${id}/balance`, { balanceCents }),
  archive: (id: string) => api.delete<Account>(`/accounts/${id}`),
  /**
   * Borrado real. El servidor lo rechaza con un 400 explicativo si la cuenta ya
   * tiene historia, así que llamarlo sin mirar `hasHistory` es seguro: nunca
   * destruye nada, solo falla.
   */
  remove: (id: string) => api.delete<{ deleted: true; id: string }>(`/accounts/${id}?permanent=true`),
}

export const categories = {
  list: () => api.get<{ categories: Category[]; tree?: unknown }>('/categories'),
}

export interface CardInput {
  name: string
  issuer?: string
  brand?: CardBrand
  /** Exactamente 4 dígitos; el servidor rechaza cualquier otra longitud. */
  last4?: string
  creditLimitCents: number
  /** Lo que ya debes en la tarjeta el día que la registras. */
  openingBalanceCents?: number
  /** 1–31. El 31 se recorta al último día en los meses que no lo tienen. */
  statementDay: number
  paymentTermDays?: number
  paymentTermType?: 'BUSINESS_DAYS' | 'CALENDAR_DAYS'
  cutoffInclusive?: boolean
  /**
   * `isShared` y `ownerMemberId` son excluyentes: el servidor rechaza que
   * vengan juntos, y sin uno de los dos no sabe de quién es la tarjeta.
   */
  isShared?: boolean
  ownerMemberId?: string
}

export interface CardPaymentResult {
  statement: StatementSummary
  transactionId: string
}

export const cards = {
  list: () => api.get<CreditCard[]>('/cards'),
  create: (input: CardInput) => api.post<CreditCard>('/cards', input),
  update: (id: string, input: Partial<CardInput>) =>
    api.patch<CreditCard>(`/cards/${id}`, input),
  archive: (id: string) => api.delete<void>(`/cards/${id}`),
  pay: (
    cardId: string,
    statementId: string,
    input: {
      amountCents: number
      /**
       * El servidor lo llama `fromAccountId`, no `accountId`. Con el nombre
       * equivocado el pago falla con un 400 de validación y el dinero nunca se
       * mueve, así que el tipo tiene que decir el nombre real.
       */
      fromAccountId: string
      /** Si se omite, el servidor toma hoy en la zona horaria del hogar. */
      occurredOn?: PlainDate
      description?: string
    },
  ) => api.post<CardPaymentResult>(`/cards/${cardId}/statements/${statementId}/pay`, input),
}

// ── Compras a meses: MSI y MCI ──────────────────────────────────────────────

/** Vivo, liquidado o cancelado. Un plan nunca se borra si ya cobró algo. */
export type InstallmentPlanStatus = 'ACTIVE' | 'SETTLED' | 'CANCELLED'

/**
 * Una mensualidad ubicada en su ciclo.
 *
 * `cutoffDate` es el corte que la cobra y `dueDate` el día en que de verdad
 * sale el dinero. Son dos fechas distintas y las dos importan: el corte decide
 * en qué estado de cuenta cae, el vencimiento decide cuándo baja el disponible.
 */
export interface PlanInstallment {
  /** 1 … `months`. */
  number: number
  amountCents: number
  cutoffDate: PlainDate
  dueDate: PlainDate
}

/** La misma mensualidad, ya con la marca que solo devuelve `/schedule`. */
export interface ScheduledInstallment extends PlanInstallment {
  /**
   * **Ya cobrada, no necesariamente pagada.**
   *
   * Quiere decir que el banco ya la cargó: venía saldada al registrar el plan,
   * cayó en un corte anterior al ancla contable, o su estado de cuenta ya
   * venció. Si ese estado de cuenta se pagó de verdad es otra pregunta, y la
   * contesta el estado de la tarjeta. Se llamaba `paid`, y por eso la pantalla
   * pintaba en verde y con «Ya pagada» una mensualidad vencida e impaga.
   */
  charged: boolean
}

/**
 * Una compra a meses tal como la devuelve la API, con todo lo derivado ya
 * resuelto por el servidor.
 *
 * **`monthlyAmountCents` es la mensualidad de verdad**: en MSI la calculó el
 * servidor repartiendo el precio, en MCI es la que captura quien la registra.
 */
export interface InstallmentPlan {
  id: string
  name: string
  creditCardId: string
  card: {
    id: string
    name: string
    brand: CardBrand
    last4: string | null
    colorToken: string | null
    ownerMemberId: string | null
    archived: boolean
  }
  categoryId: string | null
  category: { id: string; name: string; icon: string | null; colorToken: string | null } | null
  attributedToMemberId: string | null

  /** Precio de la compra: lo que dice el ticket. */
  totalAmountCents: number
  months: number
  monthlyAmountCents: number
  /** Cuántas mensualidades llevaba pagadas **al registrar el plan**. */
  monthsPaid: number
  purchasedOn: PlainDate
  /** Desde qué día responde la app por esta compra. Lo pone el servidor. */
  bookedOn: PlainDate
  /** MSI = `true`, MCI = `false`. */
  interestFree: boolean
  status: InstallmentPlanStatus
  /** Día en que se detuvo: liquidación adelantada o cancelación. */
  stoppedOn: PlainDate | null
  settlementAmountCents: number | null
  notes: string | null

  /** Precio + intereses. En MSI es el precio. */
  totalToPayCents: number
  /** Cero en MSI. En MCI, lo que se paga de más sobre el precio. */
  interestCents: number
  /** Lo que se debía al registrar el plan. No cambia con el calendario. */
  outstandingBalanceCents: number
  /** Lo que falta hoy. Un plan detenido ya no debe nada: cero. */
  remainingBalanceCents: number
  paidCount: number
  remainingCount: number
  /** Avance por mensualidades, no por dinero: la gente cuenta «voy 3 de 12». */
  progressPct: number
  /**
   * La siguiente mensualidad. **Se llama `nextInstallment`, no `next`**, y
   * `lastInstallment`, no `last`: con el nombre corto TypeScript no se queja y
   * la fila queda sin mensualidad ni fecha, en silencio.
   */
  nextInstallment: PlanInstallment | null
  lastInstallment: PlanInstallment | null
  createdAt: string
  updatedAt: string
}

export interface InstallmentsSummary {
  planCount: number
  activeCount: number
  /**
   * Suma de la **siguiente** mensualidad de cada plan vivo, no de todas: es lo
   * que va a caer en los próximos cortes. Sumar el precio de las compras diría
   * que el hogar debe doce mil este mes cuando debe mil.
   */
  monthlyCommittedCents: number
  remainingBalanceCents: number
  remainingCount: number
}

export interface InstallmentsResponse {
  plans: InstallmentPlan[]
  summary: InstallmentsSummary
}

export interface InstallmentSchedule {
  plan: InstallmentPlan
  installments: ScheduledInstallment[]
}

export interface SettlementResult {
  plan: InstallmentPlan
  /** Lo que se cobrará de golpe en el estado de cuenta abierto. */
  settlementAmountCents: number
  /** Lo que se habría pagado mes a mes. La diferencia es lo que se ahorró. */
  pendingBeforeCents: number
  savedCents: number
  settledOn: PlainDate
}

/**
 * Alta de una compra a meses.
 *
 * Dos nombres que hay que respetar al pie de la letra: la tarjeta es
 * **`creditCardId`** —no `cardId`— y la persona a la que se le atribuye el gasto
 * es **`attributedToMemberId`**. El servidor valida con un esquema que descarta
 * lo que no reconoce, así que con el nombre equivocado el plan se crea colgando
 * de la nada o sin tarjeta.
 */
export interface InstallmentPlanInput {
  creditCardId: string
  name: string
  totalAmountCents: number
  /** De 2 a 60. A un mes no hay compra diferida, hay compra. */
  months: number
  /**
   * **Solo MCI.** En MSI la mensualidad la calcula el servidor a partir del
   * precio, y mandarla es un error de validación, no un dato que se ignore:
   * aceptarla dejaría a la persona creyendo que su plan cobra lo que capturó.
   */
  monthlyAmountCents?: number
  monthsPaid?: number
  purchasedOn: PlainDate
  attributedToMemberId?: string | null
  categoryId?: string | null
  /** MSI = `true`, MCI = `false`. Por omisión, MSI. */
  interestFree?: boolean
  notes?: string | null
}

export const installments = {
  list: (
    params: {
      creditCardId?: string
      attributedToMemberId?: string
      status?: InstallmentPlanStatus
      /** Por omisión el servidor esconde las canceladas; las liquidadas no. */
      includeCancelled?: boolean
    } = {},
  ) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const qs = query.toString()
    return api.get<InstallmentsResponse>(`/installments${qs ? `?${qs}` : ''}`)
  },
  get: (id: string) => api.get<InstallmentPlan>(`/installments/${id}`),
  /** Las `months` mensualidades con su corte, su vencimiento y si ya se cobraron. */
  schedule: (id: string) => api.get<InstallmentSchedule>(`/installments/${id}/schedule`),
  create: (input: InstallmentPlanInput) => api.post<InstallmentPlan>('/installments', input),
  /** La tarjeta no se puede cambiar: el servidor ignora `creditCardId` aquí. */
  update: (id: string, input: Partial<Omit<InstallmentPlanInput, 'creditCardId'>>) =>
    api.patch<InstallmentPlan>(`/installments/${id}`, input),
  /**
   * Liquidar por adelantado. Sin `amountCents`, el servidor cobra lo que aún no
   * ha cortado; con él, lo que el banco haya dicho que cobra —en MCI suele ser
   * menos, porque perdona los intereses no devengados—. Nunca puede ser mayor
   * que lo pendiente: el servidor responde 400.
   */
  settle: (id: string, input: { amountCents?: number; settledOn?: PlainDate } = {}) =>
    api.post<SettlementResult>(`/installments/${id}/settle`, input),
  /**
   * Cancela el plan. **No lo borra** si la tarjeta ya le cobró alguna
   * mensualidad: esos cortes ya cerraron y reescribirlos dejaría saldos que no
   * cuadran con el papel del banco. Responde 204 en los dos casos.
   */
  cancel: (id: string) => api.delete<void>(`/installments/${id}`),
}

/**
 * Página de movimientos.
 *
 * El servidor pagina por número de página, no por cursor: la lista se ordena
 * por `occurredOn` descendente y admite filtros arbitrarios, así que el total
 * y el número de páginas son parte de la respuesta y no se pueden deducir.
 */
export interface TransactionPage {
  items: Transaction[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export const transactions = {
  /** Un movimiento con todo lo derivado: partes de pago, splits y comprobantes. */
  get: (id: string) => api.get<Transaction>(`/transactions/${id}`),
  list: (params: {
    from?: PlainDate
    to?: PlainDate
    categoryId?: string
    /** Id de un integrante, o la palabra `joint` para el fondo común. */
    memberId?: string
    accountId?: string
    creditCardId?: string
    type?: TransactionType
    search?: string
    page?: number
    /** Máximo 100 por página; el servidor rechaza más. */
    pageSize?: number
  } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const qs = query.toString()
    return api.get<TransactionPage>(`/transactions${qs ? `?${qs}` : ''}`)
  },
  create: (input: {
    type?: string
    amountCents: number
    occurredOn: PlainDate
    description: string
    categoryId?: string
    attributedToMemberId?: string | null
    splitMode?: SplitMode
    paymentSource: PaymentSource
    accountId?: string
    creditCardId?: string
  }) => api.post<Transaction>('/transactions', input),
  remove: (id: string) => api.delete<void>(`/transactions/${id}`),
}

export const recurring = {
  // No hay `GET /recurring/:id`: la pantalla de edición toma el registro de esta
  // misma lista, que además es la que trae los campos calculados.
  list: () => api.get<{ recurring: RecurringExpense[] } | RecurringExpense[]>('/recurring'),
  create: (input: RecurringInput) => api.post<RecurringDefinition>('/recurring', input),
  update: (id: string, input: RecurringPatch) =>
    api.patch<RecurringDefinition>(`/recurring/${id}`, input),
  /** Registra la ocurrencia del periodo («ya lo pagué») y adelanta el calendario. */
  post: (id: string, input: { amountCents?: number; occurredOn?: PlainDate; notes?: string } = {}) =>
    api.post<PostOccurrenceResult>(`/recurring/${id}/post`, input),
  /** Baja lógica: se desactiva, no se borra, porque sus movimientos lo apuntan. */
  remove: (id: string) => api.delete<void>(`/recurring/${id}`),
}

export interface SavingsContribution {
  id: string
  goalId: string
  memberId: string
  memberName: string
  memberColorToken: string
  amountCents: number
  occurredOn: PlainDate
  note: string | null
  transferred: boolean
}

export const savings = {
  goals: () => api.get<{ goals: SavingsGoal[] } | SavingsGoal[]>('/savings/goals'),
  createGoal: (input: {
    name: string
    /** Omitirlo crea una meta (`GOAL`): quien no elige tipo está poniéndose una. */
    kind?: FundKind
    /**
     * Obligatorio solo en `GOAL`; el servidor rechaza una meta sin él y acepta
     * `null` en cualquier otro tipo.
     */
    targetAmountCents?: number | null
    targetDate?: PlainDate | null
    /**
     * Omitirlo crea la meta a nombre de quien la captura; `null` la crea en el
     * fondo común. Son tres estados distintos —ausente, nulo y con id— y el
     * servidor los trata distinto, así que aquí se mandan siempre explícitos.
     */
    ownerMemberId?: string | null
    linkedAccountId?: string | null
    icon?: string | null
  }) => api.post<SavingsGoal>('/savings/goals', input),
  /** El dueño de la meta no es editable: el servidor ignora `ownerMemberId`. */
  updateGoal: (goalId: string, input: {
    name?: string
    kind?: FundKind
    /** `null` quita el objetivo; el servidor solo lo permite si el tipo no es `GOAL`. */
    targetAmountCents?: number | null
    targetDate?: PlainDate | null
    linkedAccountId?: string | null
    status?: 'ACTIVE' | 'PAUSED'
  }) => api.patch<SavingsGoal>(`/savings/goals/${goalId}`, input),
  contribute: (goalId: string, input: {
    amountCents: number
    occurredOn: PlainDate
    note?: string | null
    /**
     * `false` deja la aportación comprometida: ningún saldo se mueve, pero el
     * disponible ya la descuenta. `true` hace el traspaso real y entonces el
     * servidor exige `fromAccountId` **y** que la meta tenga cuenta ligada.
     */
    transferred?: boolean
    fromAccountId?: string | null
  }) => api.post<{ contribution: SavingsContribution; goal: SavingsGoal }>(
    `/savings/goals/${goalId}/contributions`,
    input,
  ),
  /**
   * El reparto del ingreso de un integrante entre sus fondos.
   *
   * Sin `memberId` devuelve el de quien pregunta. Solo un administrador puede
   * pedir el de otra persona: cuánto ahorra alguien de lo que gana no es dato de
   * consulta libre ni dentro del mismo hogar.
   */
  allocations: (memberId?: string) =>
    api.get<AllocationPlan>(
      `/savings/allocations${memberId ? `?memberId=${encodeURIComponent(memberId)}` : ''}`,
    ),
  /**
   * Sustituye el reparto **entero** del integrante.
   *
   * Lo que no venga en la lista se queda sin porcentaje, así que hay que mandar
   * siempre el plan completo y no solo las filas que se movieron. El servidor
   * rechaza con 400 que la suma pase de 100.
   */
  replaceAllocations: (input: {
    memberId?: string
    allocations: ReadonlyArray<{ goalId: string; percent: number }>
  }) => api.put<AllocationPlan>('/savings/allocations', input),
  /**
   * Qué le tocaría a cada fondo de un ingreso concreto.
   *
   * Es `GET` a propósito: propone y no escribe una sola fila. La aportación la
   * confirma la persona desde la meta, así que esta llamada se puede repetir sin
   * miedo a duplicar dinero.
   */
  allocationPreview: (incomeEventId: string) =>
    api.get<AllocationPreview>(
      `/savings/allocations/preview?incomeEventId=${encodeURIComponent(incomeEventId)}`,
    ),
}

/**
 * Un fondo dentro del reparto de una persona.
 *
 * El porcentaje es **de quien pregunta**, no del fondo: en un fondo del hogar
 * cada integrante pone el suyo, y dos personas pueden destinarle porcentajes
 * distintos sin pisarse.
 */
export interface AllocationItem {
  goalId: string
  name: string
  kind: FundKind
  icon: string | null
  colorToken: string | null
  /** `null` = este integrante no le destina nada todavía. */
  percent: number | null
  /** `null` = fondo del hogar. */
  ownerMemberId: string | null
  /** `null` en fondos sin meta: su avance es saldo, no porcentaje. */
  targetAmountCents: number | null
  currentAmountCents: number
}

export interface AllocationPlan {
  memberId: string
  totalPercent: number
  /** Lo que queda del ingreso sin destinar. Nunca negativo. */
  freePercent: number
  items: AllocationItem[]
}

/** Un fondo de ahorro no siempre es una meta: la emergencia se mantiene, no se cumple. */
export type FundKind = 'GOAL' | 'GENERAL' | 'EMERGENCY' | 'CUSTOM'

export interface AllocationPreviewLine {
  goalId: string
  name: string
  kind: FundKind
  icon: string | null
  colorToken: string | null
  percent: number
  /** Lo que le tocaría a este fondo de este ingreso. */
  amountCents: number
  /** Lo que ya se aportó a este fondo desde este mismo ingreso. */
  alreadyContributedCents: number
  /** Lo que faltaría por aportar. Es el monto que la app propone confirmar. */
  pendingCents: number
  /** Nulo en fondos sin meta: su avance es saldo, no porcentaje. */
  targetAmountCents: number | null
  currentAmountCents: number
}

export interface AllocationPreview {
  incomeEventId: string
  memberId: string
  /**
   * `ACTUAL` cuando el ingreso ya se confirmó y se reparte el monto real;
   * `EXPECTED` cuando todavía no llega y se reparte el estimado. Importa
   * decirlo: no es lo mismo repartir un depósito que ya cayó que una comisión
   * que quizá no caiga.
   */
  basis: 'ACTUAL' | 'EXPECTED'
  status: IncomeStatus
  expectedDate: PlainDate
  baseAmountCents: number
  totalPercent: number
  /** Suma de lo repartido. Puede diferir de aplicar el total por redondeo. */
  totalCents: number
  pendingCents: number
  /** Lo que queda del ingreso después del reparto. */
  remainderCents: number
  lines: AllocationPreviewLine[]
}

// ── Revisiones conjuntas de fondos (§11) ────────────────────────────────────

/**
 * Cada cuánto se sienta el hogar a comprobar el ahorro.
 *
 * `CUSTOM` existe en el servidor y significa «la fecha se elige cada vez»: al
 * completar una revisión no programa la siguiente sola. La app no lo ofrece
 * porque «sin cadencia» ya se expresa dejando la frecuencia en `null`, y dos
 * formas de decir lo mismo solo confunden. Se tipa para poder leerlo si viene
 * de un hogar que lo tenía puesto.
 */
export type ReviewFrequency =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SEMIANNUAL'
  | 'CUSTOM'

/** Archivo que respalda una revisión. Es un adjunto visto desde la revisión. */
export interface ReviewEvidence {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  /** Lo que ese papel demuestra, si quien lo subió lo declaró. */
  declaredAmountCents: number | null
  uploadedByMemberId: string
  createdAt: string
  url: string
}

export interface Review {
  id: string
  scheduledFor: PlainDate
  /** Nulo mientras está pendiente. */
  completedAt: string | null
  notes: string | null
  /**
   * Lo que la app decía que había, **congelado al programar la revisión**. Si se
   * recalculara al completarla, la comparación no probaría nada: cualquier
   * movimiento registrado entre medias movería el número contra el que se
   * compara y siempre cuadraría por construcción.
   */
  expectedTotalCents: number | null
  /** Lo que se comprobó que hay de verdad. */
  confirmedTotalCents: number | null
  /** Confirmado menos esperado. Lo calcula el servidor para que nadie reste al revés. */
  differenceCents: number | null
  createdAt: string
  evidence: ReviewEvidence[]
}

export interface ReviewsOverview {
  today: PlainDate
  /**
   * Falso en hogares de una sola persona. La revisión conjunta es un acto de
   * confianza entre dos: revisarse uno mismo no demuestra nada, y el servidor
   * rechaza programarla.
   */
  available: boolean
  frequency: ReviewFrequency | null
  next: Review | null
  /** Completadas, de la más reciente a la más vieja. Máximo 24. */
  history: Review[]
  /** Lo que la app dice que hay ahorrado ahora mismo. */
  savedNowCents: number
}

export const reviews = {
  overview: () => api.get<ReviewsOverview>('/reviews'),
  /** Solo puede haber una pendiente a la vez; el servidor rechaza la segunda. */
  schedule: (input: { scheduledFor: PlainDate; notes?: string | null }) =>
    api.post<Review>('/reviews', input),
  /** Devuelve también la siguiente, que el servidor programa en la misma transacción. */
  complete: (id: string, input: { confirmedTotalCents: number; notes?: string | null }) =>
    api.post<{ review: Review; next: Review | null }>(`/reviews/${id}/complete`, input),
  /** Requiere rol de administración. `null` apaga las revisiones sin borrar el historial. */
  setFrequency: (reviewFrequency: ReviewFrequency | null) =>
    api.patch<ReviewsOverview>('/reviews/settings', { reviewFrequency }),
}

export interface PayrollProfile {
  frequency: PayFrequency
  netAmountCents: number
  anchorDayA: number
  anchorDayB: number | null
  anchorDate: PlainDate | null
  nonBusinessDayRule: NonBusinessDayRule
  defaultSavingsRatePct: number
}

export type IncomeKind = 'FIXED' | 'VARIABLE'

/**
 * Una fuente de ingreso. Cada quien puede tener varias.
 *
 * Antes esto era un único `PayrollProfile` por persona, lo que dejaba invisible
 * la mitad del dinero de quien cobra sueldo y además factura. `kind` separa lo
 * que llega siempre de lo que llega a veces: un ingreso `VARIABLE` no cuenta
 * para el disponible hasta que se confirma, porque contar con una comisión
 * antes de recibirla es la forma más común de sobregirarse.
 */
export interface IncomeSource {
  id: string
  memberId: string
  name: string
  kind: IncomeKind
  frequency: PayFrequency
  netAmountCents: number
  anchorDayA: number
  anchorDayB: number | null
  anchorDate: PlainDate | null
  nonBusinessDayRule: NonBusinessDayRule
  defaultSavingsRatePct: number
  active: boolean
  nextPayDates: PlainDate[]
  /**
   * Solo en los ingresos sin calendario: lo que de verdad se cobró en los
   * últimos meses. Es lo que sustituye al «esperado» cuando no hay uno.
   */
  historial: {
    meses: number
    vecesCobrado: number
    totalCents: number
    /** Total entre los meses mirados, no entre las veces que se cobró. */
    promedioMensualCents: number
  } | null
  suggestedSavingsCents: number
  scheduleError: string | null
}

export interface IncomeSourceInput {
  name: string
  kind?: IncomeKind
  frequency: PayFrequency
  netAmountCents: number
  anchorDayA: number
  anchorDayB?: number | null
  anchorDate?: PlainDate | null
  nonBusinessDayRule?: NonBusinessDayRule
  defaultSavingsRatePct?: number
  active?: boolean
}

export type IncomeStatus = 'SCHEDULED' | 'CONFIRMED' | 'SKIPPED'
export type VarianceOutcome = 'PENDING' | 'EXACT' | 'ABOVE' | 'BELOW' | 'MISSED'

/**
 * Objetivo de ahorro contra lo que se decidió apartar.
 *
 * La diferencia y el «se cumplió» cuelgan de `outcome` y no del propio
 * `savings`: mientras nadie conteste, `outcome` viene nulo entero. Aplanarlos
 * obligaría a inventar un `met: null` que en cuanto alguien olvide comprobar el
 * nulo se lee como «no cumpliste» —un regaño por una pregunta sin responder.
 */
export interface IncomeSavingsOutcome {
  targetCents: number
  savedCents: number
  deltaCents: number
  /** Avance sobre el objetivo. Sin tope: ahorrar de más tiene que notarse. */
  percent: number
  met: boolean
}

export interface IncomeEventSavings {
  /** El porcentaje configurado en la fuente, aplicado a lo esperado. */
  targetCents: number
  /** Lo que se decidió apartar. Nulo mientras nadie conteste. */
  decidedCents: number | null
  decidedAt: string | null
  /** Lo que de verdad aterrizó en metas citando esta nómina. */
  contributedCents: number
  outcome: IncomeSavingsOutcome | null
}

/** Una nómina esperada, con lo que se dijo que llegaría y lo que llegó. */
export interface IncomeEvent {
  id: string
  memberId: string
  incomeSourceId: string
  sourceName: string
  kind: IncomeKind
  status: IncomeStatus
  expectedDate: PlainDate
  actualDate: PlainDate | null
  expectedAmountCents: number
  actualAmountCents: number | null
  periodStart: PlainDate
  periodEnd: PlainDate
  variance: {
    expectedCents: number
    actualCents: number | null
    deltaCents: number | null
    percentOfExpected: number | null
    outcome: VarianceOutcome
  }
  savings: IncomeEventSavings
}

export const incomes = {
  /** Las fuentes cuelgan del integrante: el hogar se arma antes de que todos entren. */
  list: (memberId: string) =>
    api.get<IncomeSource[]>(`/household/members/${memberId}/incomes`),
  create: (memberId: string, input: IncomeSourceInput) =>
    api.post<IncomeSource>(`/household/members/${memberId}/incomes`, input),
  update: (memberId: string, id: string, input: Partial<IncomeSourceInput>) =>
    api.patch<IncomeSource>(`/household/members/${memberId}/incomes/${id}`, input),
  remove: (memberId: string, id: string) =>
    api.delete<void>(`/household/members/${memberId}/incomes/${id}`),

  /**
   * Dinero que ya cayó y se registra a mano.
   *
   * Sin `incomeSourceId` es un ingreso extra de una sola vez —un bono, la venta
   * de algo—. Con él, es un cobro de una fuente sin calendario y alimenta su
   * promedio de los últimos meses.
   */
  received: (input: {
    amountCents: number
    occurredOn: PlainDate
    depositToAccountId: string
    memberId?: string
    incomeSourceId?: string | null
    description?: string | null
  }) => api.post<IncomeEvent | { transactionId: string }>('/incomes/received', input),

  events: {
    list: (params: { status?: IncomeStatus; upTo?: PlainDate; memberId?: string } = {}) => {
      const query = new URLSearchParams()
      for (const [k, v] of Object.entries(params)) if (v) query.set(k, String(v))
      const qs = query.toString()
      return api.get<IncomeEvent[]>(`/incomes/events${qs ? `?${qs}` : ''}`)
    },
    /** Una sola nómina. La pantalla de confirmación entra por aquí, no por la lista. */
    get: (id: string) => api.get<IncomeEvent>(`/incomes/events/${id}`),
    /**
     * Confirma cuánto llegó de verdad. `received: false` lo marca como no recibido.
     *
     * La cuenta destino se llama `depositToAccountId`, **no** `accountId`: el
     * servidor valida con un esquema que descarta en silencio lo que no
     * reconoce, así que con el nombre equivocado la petición responde 200, el
     * ingreso queda confirmado y ningún saldo se mueve. Un fallo mudo.
     */
    confirm: (
      id: string,
      input: {
        received: boolean
        /** Obligatorio cuando sí llegó; el servidor lo rechaza si falta. */
        actualAmountCents?: number
        /** Si se omite, el servidor toma la fecha esperada. */
        actualDate?: PlainDate
        /** Sin ella no se toca ningún saldo: solo se registra el hecho. */
        depositToAccountId?: string | null
      },
    ) => api.post<IncomeEvent>(`/incomes/events/${id}/confirm`, input),
    /** Responde a «tu objetivo era ahorrar X, ¿se cumplió?». */
    savings: (id: string, savedCents: number) =>
      api.post<IncomeEvent>(`/incomes/events/${id}/savings`, { savedCents }),
  },
}

// ── Bandeja de avisos ───────────────────────────────────────────────────────

/**
 * Qué tan pronto hay que hacer algo. Es la clave de orden de la bandeja y por
 * eso no es un número de días: nadie ordena su cabeza por fechas, sino por
 * «esto ya se me pasó», «esto es hoy», «esto viene» y «esto solo es para que lo
 * sepas».
 */
export type UrgenciaAviso = 'VENCIDO' | 'HOY' | 'PRONTO' | 'INFO'

export type TipoAviso =
  | 'NOMINA_SIN_CONFIRMAR'
  | 'TARJETA_POR_PAGAR'
  | 'FIJO_PENDIENTE'
  | 'SIN_JUSTIFICAR'
  | 'META_ALCANZADA'
  | 'REVISION_PROGRAMADA'

export interface AccionAviso {
  /** Texto del botón, en imperativo: «Confirmar», «Pagar». */
  etiqueta: string
  /** Ruta del cliente a la que lleva el toque. */
  ruta: string
}

export interface AvisoBandeja {
  /** `tipo:idDeLaEntidad`. La bandeja no materializa filas: el identificador
   *  sale de lo que lo origina y por eso es estable entre lecturas. */
  id: string
  tipo: TipoAviso
  titulo: string
  detalle: string
  fecha: PlainDate
  urgencia: UrgenciaAviso
  /** Nulo cuando no hay nada que hacer: una meta alcanzada solo se celebra. */
  accion: AccionAviso | null
  memberId: string | null
  personColorToken: string | null
  /** Entró después de la última vez que se abrió la bandeja. */
  nuevo: boolean
}

export interface ActividadBandeja {
  id: string
  /** `transaction.created`, `income.confirmed`… */
  accion: string
  entityType: string
  entityId: string
  resumen: string
  autor: string
  memberId: string | null
  personColorToken: string | null
  /** ISO con hora, no `PlainDate`: la actividad se lee por minutos. */
  createdAt: string
  nuevo: boolean
}

export interface BandejaAvisos {
  today: PlainDate
  moneda: string
  resumen: {
    /** Renglones que piden una acción. Es el número de la campanita. */
    pendientes: number
    /** Renglones aparecidos desde la última apertura. De aquí sale el punto. */
    sinVer: number
  }
  /** Última vez que este integrante abrió la bandeja. Nulo = nunca. */
  vistoEn: string | null
  avisos: AvisoBandeja[]
  actividad: ActividadBandeja[]
}

// ── Configuración de avisos ─────────────────────────────────────────────────

export type NotificationType =
  | 'PAYDAY_SAVINGS_PROMPT'
  | 'CARD_CUTOFF'
  | 'CARD_DUE'
  | 'RECURRING_DUE'
  | 'GOAL_MILESTONE'
  | 'WEEKLY_SUMMARY'
  | 'PARTNER_ACTIVITY'
  | 'CUSTOM'

export type NotificationChannel = 'PUSH' | 'EMAIL'

/**
 * Configuración vigente de un tipo de aviso.
 *
 * `source` dice de dónde salió: `FACTORY` es lo que trae el hogar de nacimiento,
 * `HOUSEHOLD` un ajuste que puso quien administra para todos y `MEMBER` uno
 * propio. Se enseña porque apagar algo que sigue llegando —porque lo manda el
 * ajuste del hogar— es la forma más rápida de perder la confianza en la pantalla.
 */
export interface ReglaAviso {
  type: NotificationType
  enabled: boolean
  channels: NotificationChannel[]
  /** Días de anticipación con los que se avisa. `0` = el mismo día. */
  leadDays: number[]
  /** `HH:MM` en 24 horas. */
  timeOfDay: string
  quietHoursStart: string | null
  quietHoursEnd: string | null
  source: 'FACTORY' | 'HOUSEHOLD' | 'MEMBER'
}

export type ReminderScheduleType = 'ONCE' | 'RECURRING' | 'RELATIVE_TO_ENTITY'
export type ReminderFrequency =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'SEMI_MONTHLY'
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'YEARLY'
export type ReminderEntityType =
  | 'CREDIT_CARD_DUE'
  | 'CREDIT_CARD_CUTOFF'
  | 'RECURRING_EXPENSE'
  | 'INCOME_EVENT'
  | 'SAVINGS_GOAL'

export interface Recordatorio {
  id: string
  title: string
  body: string | null
  /** Vacío = a todo el hogar. */
  targetMemberIds: string[]
  channels: NotificationChannel[]
  scheduleType: ReminderScheduleType
  runAt: PlainDate | null
  frequency: ReminderFrequency | null
  dayOfMonth: number | null
  /** 0 = domingo, como `Date.getDay()`. */
  dayOfWeek: number | null
  timeOfDay: string
  entityType: ReminderEntityType | null
  entityId: string | null
  offsetDays: number | null
  actions: Array<'MARK_PAID' | 'SNOOZE' | 'OPEN'>
  enabled: boolean
  lastFiredAt: string | null
  createdByMemberId: string
  createdAt: string
  updatedAt: string
}

export interface RecordatorioInput {
  title: string
  body?: string | null
  targetMemberIds?: string[]
  channels?: NotificationChannel[]
  scheduleType: ReminderScheduleType
  runAt?: PlainDate | null
  frequency?: ReminderFrequency | null
  dayOfMonth?: number | null
  dayOfWeek?: number | null
  timeOfDay?: string
  entityType?: ReminderEntityType | null
  entityId?: string | null
  offsetDays?: number | null
  actions?: Array<'MARK_PAID' | 'SNOOZE' | 'OPEN'>
  enabled?: boolean
}

export const notifications = {
  registrarDispositivo: (input: {
    platform: 'IOS' | 'ANDROID' | 'WEB'
    endpoint?: string
    p256dh?: string
    auth?: string
    fcmToken?: string
    deviceName?: string
  }) => api.post<{ id: string }>('/notifications/devices', input),
  bajaDispositivo: (id: string) => api.delete<void>(`/notifications/devices/${id}`),

  /** La bandeja entera: lo que pide acción, lo que pasó y el resumen. */
  bandeja: () => api.get<BandejaAvisos>('/notifications'),
  /**
   * Apaga el punto rojo.
   *
   * `hasta` es el instante en que el cliente **cargó** la bandeja, no el del
   * toque: entre una cosa y otra pudo entrar un movimiento de la pareja, y
   * darlo por leído lo escondería sin que nadie lo haya visto.
   */
  marcarBandejaVista: (hasta?: string) =>
    api.post<{ vistoEn: string }>('/notifications/seen', hasta ? { hasta } : {}),

  reglas: () => api.get<ReglaAviso[]>('/notifications/rules'),
  /**
   * `scope: 'ME'` ajusta los avisos de quien toca; `'HOUSEHOLD'` los de todos y
   * exige rol de administración. Lo omitido se queda como estaba.
   */
  actualizarRegla: (
    type: NotificationType,
    input: {
      scope?: 'ME' | 'HOUSEHOLD'
      enabled?: boolean
      channels?: NotificationChannel[]
      leadDays?: number[]
      timeOfDay?: string
      quietHoursStart?: string | null
      quietHoursEnd?: string | null
    },
  ) => api.patch<unknown>(`/notifications/rules/${type}`, { scope: 'ME', ...input }),

  recordatorios: () => api.get<Recordatorio[]>('/notifications/reminders'),
  crearRecordatorio: (input: RecordatorioInput) =>
    api.post<Recordatorio>('/notifications/reminders', input),
  actualizarRecordatorio: (id: string, input: Partial<RecordatorioInput>) =>
    api.patch<Recordatorio>(`/notifications/reminders/${id}`, input),
  borrarRecordatorio: (id: string) => api.delete<void>(`/notifications/reminders/${id}`),

  /** Acuse de una notificación concreta. Lo llama el service worker. */
  marcarVisto: (id: string) => api.post<void>(`/notifications/${id}/seen`),
}

export interface CashflowResponse {
  today: PlainDate
  periodStart: PlainDate
  periodEnd: PlainDate
  currency: string
  months: Array<{ month: string; incomeCents: number; expenseCents: number; netCents: number }>
  currentMonth: {
    month: string
    totalExpenseCents: number
    byCategory: Array<{
      categoryId: string
      name: string
      icon: string | null
      colorToken: string | null
      amountCents: number
      percent: number
    }>
  }
}

export const dashboard = {
  cashflow: (months = 6) => api.get<CashflowResponse>(`/dashboard/cashflow?months=${months}`),
  get: (scope?: 'combined' | 'joint' | 'personal', memberId?: string) => {
    const query = new URLSearchParams()
    if (scope) query.set('scope', scope)
    if (memberId) query.set('memberId', memberId)
    const qs = query.toString()
    return api.get<DashboardResponse>(`/dashboard${qs ? `?${qs}` : ''}`)
  },
}

// ── Justificación del dinero (§12) ──────────────────────────────────────────

/** Un gasto fijo que vence en el periodo y todavía nadie ha registrado. */
export interface FijoPendiente {
  id: string
  nombre: string
  montoCents: number
  vencimiento: PlainDate
}

/**
 * Respuesta de `GET /api/reconciliation`.
 *
 * `porcentajeIdentificado` cuenta lo registrado **más** el saldo que sigue en
 * las cuentas: el dinero que no se ha gastado está localizado, no perdido. Por
 * eso el desglose de tres conceptos no suma el identificado por sí solo, y la
 * pantalla tiene que enseñar también el saldo para que la cuenta cierre a la
 * vista.
 */
export interface ReconciliationSummary {
  periodo: { inicio: PlainDate; fin: PlainDate }
  moneda: string
  /** Ingresos ya confirmados del periodo. */
  recibidoCents: number
  /** Gastos + ahorro + pagos de tarjeta que salieron de una cuenta. */
  registradoCents: number
  desglose: {
    gastosCents: number
    ahorroCents: number
    pagosTarjetaCents: number
  }
  /** Suma de las cuentas de uso corriente: lo que se tiene ahora mismo. */
  saldoActualCents: number
  sinJustificarCents: number
  porcentajeIdentificado: number
  fijosPendientes: FijoPendiente[]
}

export const reconciliation = {
  summary: () => api.get<ReconciliationSummary>('/reconciliation'),
  /**
   * Marca un fijo como pagado. Responde con el resumen **ya recalculado**, así
   * que quien lo llama no necesita volver a pedirlo para mover el anillo.
   *
   * `amountCents` solo hace falta en los recibos de monto variable; sin él, el
   * servidor responde 400 pidiendo el importe real.
   */
  markPaid: (input: {
    recurringExpenseId: string
    amountCents?: number
    occurredOn?: PlainDate
    notes?: string
  }) => api.post<ReconciliationSummary>('/reconciliation/mark-paid', input),
}

// ── Plan contra realidad (§27) ──────────────────────────────────────────────

/**
 * Las cifras van sin sufijo `Cents` porque así las nombra la API, pero son
 * centavos enteros como todo lo demás.
 */
export interface PlanVsRealFigures {
  ingresoEsperado: number
  ingresoReal: number
  ahorroEsperado: number
  ahorroReal: number
  gastoEstimado: number
  gastoReal: number
  aportacionEsperada: number
  aportacionReal: number
}

export interface PlanVsRealRow extends PlanVsRealFigures {
  /** `null` = el fondo común, que no es de nadie en particular. */
  memberId: string | null
  displayName: string
  personColorToken: string | null
}

export interface PlanVsRealMonth {
  /** `AAAA-MM`. */
  month: string
  inicio: PlainDate
  fin: PlainDate
  integrantes: PlanVsRealRow[]
  total: PlanVsRealFigures
}

export interface PlanVsRealReport {
  today: PlainDate
  moneda: string
  /** De más antiguo a más reciente; el último es el mes en curso. */
  meses: PlanVsRealMonth[]
}

export const reports = {
  /** Máximo 24 meses; el servidor rechaza más. */
  planVsReal: (months = 6) =>
    api.get<PlanVsRealReport>(`/reports/plan-vs-real?months=${months}`),
}

/**
 * Normaliza respuestas que unos módulos devuelven envueltas y otros como array.
 *
 * Los módulos los construyeron agentes distintos en paralelo y esa diferencia se
 * coló. Se absorbe aquí, en un solo lugar, en vez de repetir la comprobación en
 * cada pantalla.
 */
export function unwrap<T>(value: unknown, key: string): T[] {
  if (Array.isArray(value)) return value as T[]
  if (value && typeof value === 'object') {
    const inner = (value as Record<string, unknown>)[key]
    if (Array.isArray(inner)) return inner as T[]
  }
  return []
}
